import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'

import type { IdcCategorization } from './idc-categorization'

export type Function = core.common.Identified<Function.Id> &
  core.common.Named &
  core.common.Described.Optionally &
  core.common.Coded & {
    isExclusive: boolean
    isRequiredForMatchingPayroll: boolean
    isSponsored: boolean
    idcCategorization?: IdcCategorization
  }

export namespace Function {
  export type Id = core.dms.data.Record.Id

  export namespace Id {
    export const isId = core.dms.data.Record.Id.isId
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Application-owned Space Functions with stable identities.
   */
  export namespace Standard {
    /**
     * Code of a standard Space Function.
     */
    export enum Code {
      Vacant = 'VAC',
      Unassignable = 'UN',
    }

    /**
     * ID of a standard Space Function.
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id<TCode extends Code = Code> = Satisfies<
      PartialId.Standard<TCode>,
      Function.Id
    >
    export namespace Id {
      export const Vacant = from(Code.Vacant)
      export const Unassignable = from(Code.Unassignable)

      /**
       * Produces the stable record ID for a standard Space Function code.
       */
      export function from<TCode extends Code>(code: TCode): Id<TCode> {
        return PartialId.Standard.create(code)
      }
    }

    /**
     * ID of a standard Space Function that is exclusive — i.e. cannot be
     * combined with other function allocations on the same record.
     */
    export type ExclusiveId = Id<Code.Vacant> | Id<Code.Unassignable>

    /** IDs of the standard exclusive Space Functions. */
    export const EXCLUSIVE_IDS: ReadonlySet<ExclusiveId> = new Set([
      Id.Vacant,
      Id.Unassignable,
    ])

    /**
     * Checks whether a Function ID is one of the standard exclusive functions
     */
    export const isExclusiveId: TypeChecker<ExclusiveId> =
      TypeChecker.Factory.oneOf(EXCLUSIVE_IDS)

    /** Identifies a standard Space Function by stable ID. */
    export function classify(id: Function.Id): Code | undefined {
      switch (id) {
        case Id.Vacant:
          return Code.Vacant
        case Id.Unassignable:
          return Code.Unassignable
        default:
          return undefined
      }
    }
  }
}
